/** Short-term Care Pass covers at most one month of continuous caregiver days. */
export const SHORT_TERM_MAX_CONTINUOUS_DAYS = 30;

/**
 * Allowed customer booking time windows for 12-hour Care Attendant shifts.
 * Backend mirrors this in nemacareBE/config/careShiftWindows.js — keep in sync.
 */

export const SHIFT_12_DAY_WINDOWS = [
  { start: "07:00", end: "19:00", label: "07:00 AM – 07:00 PM" },
  { start: "08:00", end: "20:00", label: "08:00 AM – 08:00 PM" },
  { start: "09:00", end: "21:00", label: "09:00 AM – 09:00 PM" },
];

export const SHIFT_12_NIGHT_WINDOWS = [
  { start: "19:00", end: "07:00", overnight: true, label: "07:00 PM – 07:00 AM next day" },
  { start: "20:00", end: "08:00", overnight: true, label: "08:00 PM – 08:00 AM next day" },
  { start: "21:00", end: "09:00", overnight: true, label: "09:00 PM – 09:00 AM next day" },
];

/** Hourly bookings must start and end within this same-day window. */
export const SHIFT_HOURLY_RANGE = { startMin: "07:00", startMax: "21:00", endMin: "07:00", endMax: "21:00" };

/** 24-hour / live-in bookings may only *start* within this window (end is +24h). */
export const SHIFT_24H_START_RANGE = { startMin: "08:00", startMax: "18:00" };

/** Canonicalize marketplace / booking shift tokens to hourly | 12_day | 12_night | 24h. */
export function resolveCanonicalSearchShift(searchShift) {
  if (searchShift == null || searchShift === "") return "";
  const raw = String(searchShift).trim().toLowerCase();
  if (!raw) return "";
  if (raw === "hourly" || raw === "hour" || raw === "4" || raw === "4h") return "hourly";
  if (raw === "12_day" || raw === "12day" || raw === "day" || (raw.includes("12") && raw.includes("day"))) {
    return "12_day";
  }
  if (
    raw === "12_night" ||
    raw === "12night" ||
    raw === "night" ||
    (raw.includes("12") && raw.includes("night"))
  ) {
    return "12_night";
  }
  if (
    raw === "24h" ||
    raw === "24" ||
    raw === "24hour" ||
    raw === "24hours" ||
    raw.includes("live") ||
    (raw.includes("24") && !raw.includes("12"))
  ) {
    return "24h";
  }
  if (Number(raw) === 4) return "hourly";
  if (Number(raw) === 12) return "12_day";
  if (Number(raw) === 24) return "24h";
  return raw;
}

export function getAllowedWindowsForSearchShift(searchShift) {
  const shift = resolveCanonicalSearchShift(searchShift);
  if (shift === "12_day") return SHIFT_12_DAY_WINDOWS.map((w) => ({ ...w }));
  if (shift === "12_night") return SHIFT_12_NIGHT_WINDOWS.map((w) => ({ ...w }));
  return null;
}

export function normalizeHhMm(value) {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m) || h > 23 || m > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function timeToMinutes(hhmm) {
  const norm = normalizeHhMm(hhmm);
  if (!norm) return null;
  const [h, m] = norm.split(":").map(Number);
  return h * 60 + m;
}

function isWithinRange(hhmm, minHhmm, maxHhmm) {
  const t = timeToMinutes(hhmm);
  const min = timeToMinutes(minHhmm);
  const max = timeToMinutes(maxHhmm);
  if (t == null || min == null || max == null) return false;
  return t >= min && t <= max;
}

/** True when start/end match an allowed window for the shift (overnight aware). */
export function isAllowedShiftWindow({ searchShift, startTime, endTime }) {
  const shift = resolveCanonicalSearchShift(searchShift);
  const start = normalizeHhMm(startTime);
  const end = normalizeHhMm(endTime);

  if (shift === "12_day" || shift === "12_night") {
    const windows = getAllowedWindowsForSearchShift(shift);
    if (!windows?.length) return true;
    if (!start || !end) return false;
    return windows.some((w) => w.start === start && w.end === end);
  }
  if (shift === "24h") {
    if (!start) return false;
    return isWithinRange(start, SHIFT_24H_START_RANGE.startMin, SHIFT_24H_START_RANGE.startMax);
  }
  if (shift === "hourly") {
    if (!start) return false;
    if (!isWithinRange(start, SHIFT_HOURLY_RANGE.startMin, SHIFT_HOURLY_RANGE.startMax)) return false;
    if (end && !isWithinRange(end, SHIFT_HOURLY_RANGE.endMin, SHIFT_HOURLY_RANGE.endMax)) return false;
    return true;
  }
  return true;
}

/** Care Pass plan ids applicable to a marketplace search shift (and optional booking length). */
export function getRequiredCarePassPlanIdsForShift(searchShift, { bookingDays = null } = {}) {
  const shift = resolveCanonicalSearchShift(searchShift);
  if (shift === "hourly") return ["hourly"];
  const days = Number(bookingDays);
  const needsQuarterly =
    Number.isFinite(days) && days > SHORT_TERM_MAX_CONTINUOUS_DAYS;
  // 12h / 24h: Short-term ≤1 month; continuous >1 month → Quarterly only.
  if (shift === "12_day" || shift === "12_night" || shift === "24h") {
    return needsQuarterly ? ["long"] : ["short", "long"];
  }
  if (needsQuarterly) return ["long"];
  // Unknown / missing shift: any paid Care Pass type is acceptable.
  return ["long", "hourly", "short"];
}

/** Normalize stored plan codes (short / SHORT_TERM / quarterly / etc.) to catalog ids. */
export function normalizeCarePassPlanId(planId, planCode) {
  const raw = String(planId || planCode || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (!raw) return "";
  if (raw === "hourly" || raw.startsWith("hourly")) return "hourly";
  if (
    raw === "short" ||
    raw.startsWith("short") ||
    raw.includes("fortnight") ||
    raw.includes("monthly") ||
    raw.includes("minicare") ||
    raw.includes("caregiverday")
  ) {
    return "short";
  }
  if (
    raw === "long" ||
    raw === "quarterly" ||
    raw.startsWith("quarter") ||
    raw.includes("continuous") ||
    // Legacy plan_code used for quarterly continuous access.
    raw === "carepass"
  ) {
    return "long";
  }
  if (raw === "free") return "free";
  return String(planId || planCode || "")
    .trim()
    .toLowerCase();
}

function resolvePlanFields(subscription) {
  if (!subscription || typeof subscription !== "object") {
    return { planId: "", planCode: "", active: false, careDaysRemaining: null };
  }
  const nested =
    subscription.subscription && typeof subscription.subscription === "object"
      ? subscription.subscription
      : null;
  const meta =
    (subscription.metadata && typeof subscription.metadata === "object"
      ? subscription.metadata
      : null) ||
    (nested?.metadata && typeof nested.metadata === "object" ? nested.metadata : null) ||
    {};
  const planId =
    subscription.planId ||
    meta.planId ||
    nested?.planId ||
    meta.plan_id ||
    "";
  const planCode =
    subscription.planCode ||
    nested?.planCode ||
    meta.planCode ||
    meta.plan_code ||
    "";
  const active =
    subscription.active === true ||
    subscription.active === "true" ||
    nested?.active === true ||
    nested?.active === "true";
  const careDaysRemaining =
    subscription.careDaysRemaining != null
      ? subscription.careDaysRemaining
      : meta.careDaysRemaining != null
        ? meta.careDaysRemaining
        : null;
  return { planId, planCode, active, careDaysRemaining };
}

export function isCarePassApplicableToShift({ planId, planCode, searchShift, bookingDays = null }) {
  const allowed = getRequiredCarePassPlanIdsForShift(searchShift, { bookingDays });
  const id = normalizeCarePassPlanId(planId, planCode);
  if (!id) return false;
  return allowed.includes(id);
}

function passCoversShift(pass, searchShift, bookingDays = null) {
  if (!pass || typeof pass !== "object") return false;
  const { planId, planCode, active, careDaysRemaining } = resolvePlanFields(pass);
  if (!active) return false;
  const normalized = normalizeCarePassPlanId(planId, planCode);
  if (careDaysRemaining != null && Number(careDaysRemaining) <= 0 && normalized === "short") {
    return false;
  }
  const bookingsRemaining =
    pass.bookingsRemaining != null
      ? pass.bookingsRemaining
      : pass.metadata?.bookingsRemaining != null
        ? pass.metadata.bookingsRemaining
        : null;
  if (bookingsRemaining != null && Number(bookingsRemaining) <= 0 && normalized === "hourly") {
    return false;
  }
  const days = Number(bookingDays);
  if (
    Number.isFinite(days) &&
    days > 0 &&
    normalized === "short" &&
    careDaysRemaining != null &&
    Number(careDaysRemaining) < days
  ) {
    return false;
  }
  if (
    Number.isFinite(days) &&
    days > 0 &&
    normalized === "hourly" &&
    bookingsRemaining != null &&
    Number(bookingsRemaining) < days
  ) {
    return false;
  }
  return isCarePassApplicableToShift({ planId, planCode, searchShift, bookingDays });
}

/** True when subscription status covers the selected marketplace shift (and booking length). */
export function patientHasValidCarePassForShift(subscription, searchShift, bookingDays = null) {
  if (!subscription || typeof subscription !== "object") return false;
  const passes = Array.isArray(subscription.passes) ? subscription.passes : null;
  if (passes?.length) {
    return passes.some((pass) => passCoversShift(pass, searchShift, bookingDays));
  }
  return passCoversShift(subscription, searchShift, bookingDays);
}

/** Best remaining caregiver-day balance across active short-term passes (null if unknown). */
export function getCarePassDaysRemaining(subscription) {
  if (!subscription || typeof subscription !== "object") return null;
  const passes = Array.isArray(subscription.passes) ? subscription.passes : null;
  const rows = passes?.length ? passes : [subscription];
  let best = null;
  for (const pass of rows) {
    const { active, careDaysRemaining, planId, planCode } = resolvePlanFields(pass);
    if (!active) continue;
    const normalized = normalizeCarePassPlanId(planId, planCode);
    if (normalized !== "short" && normalized !== "long") continue;
    if (careDaysRemaining == null) continue;
    const n = Number(careDaysRemaining);
    if (!Number.isFinite(n)) continue;
    best = best == null ? n : Math.max(best, n);
  }
  return best;
}

/** Best remaining hourly visit balance (null if unknown / not hourly). */
export function getHourlyBookingsRemaining(subscription) {
  if (!subscription || typeof subscription !== "object") return null;
  const passes = Array.isArray(subscription.passes) ? subscription.passes : null;
  const rows = passes?.length ? passes : [subscription];
  let best = null;
  for (const pass of rows) {
    const { active, planId, planCode } = resolvePlanFields(pass);
    if (!active) continue;
    if (normalizeCarePassPlanId(planId, planCode) !== "hourly") continue;
    const bookingsRemaining =
      pass.bookingsRemaining != null
        ? pass.bookingsRemaining
        : pass.metadata?.bookingsRemaining != null
          ? pass.metadata.bookingsRemaining
          : null;
    if (bookingsRemaining == null) continue;
    const n = Number(bookingsRemaining);
    if (!Number.isFinite(n)) continue;
    best = best == null ? n : Math.max(best, n);
  }
  return best;
}

function hourlyBookingLabel(count) {
  const n = Math.max(0, Math.floor(Number(count)) || 0);
  return `${n} booking${n === 1 ? "" : "s"}`;
}

/**
 * Hourly recurring: each selected day / occurrence consumes 1 Hourly Pass booking.
 * One-time / instant hourly bookings consume 1.
 */
export function resolveHourlyRequiredBookings({
  pattern,
  daysServiceRequired = 1,
  selectedDates = [],
} = {}) {
  if (String(pattern || "").toLowerCase() !== "recurring") return 1;
  const days = Math.max(1, Math.floor(Number(daysServiceRequired)) || 1);
  const listed = Array.isArray(selectedDates)
    ? selectedDates.map((d) => String(d || "").slice(0, 10)).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    : [];
  const uniqueListed = new Set(listed).size;
  if (uniqueListed > 0) return Math.max(days, uniqueListed);
  return days;
}

/**
 * Customer-facing entitlement snapshot for Care Pass UX.
 * required = booking duration/visits; remaining = what the current pass still covers.
 */
export function getCarePassEntitlementSummary(subscription, searchShift, bookingDays = null) {
  const shift = resolveCanonicalSearchShift(searchShift);
  const required = Math.max(1, Number(bookingDays) || 1);
  const daysRemaining = getCarePassDaysRemaining(subscription);
  const bookingsRemaining = getHourlyBookingsRemaining(subscription);
  const covers = patientHasValidCarePassForShift(subscription, searchShift, bookingDays);
  const remaining =
    shift === "hourly"
      ? bookingsRemaining
      : daysRemaining;
  const unit = shift === "hourly" ? "booking" : "day";
  const requiredLabel =
    shift === "hourly"
      ? hourlyBookingLabel(required)
      : `${required} ${unit}${required === 1 ? "" : "s"}`;
  const remainingLabel =
    remaining == null
      ? "Unknown"
      : shift === "hourly"
        ? `${hourlyBookingLabel(remaining)} available`
        : `${remaining} ${unit}${remaining === 1 ? "" : "s"} left`;
  return {
    shift,
    required,
    remaining,
    covers,
    unit,
    requiredLabel,
    remainingLabel,
    shortfall:
      remaining != null && Number.isFinite(required) && remaining < required
        ? required - remaining
        : null,
  };
}

/** Prefer the smallest short-term pack that covers required caregiver days. */
export function preferredShortTermPackIdForDays(bookingDays, options = []) {
  const need = Math.max(1, Number(bookingDays) || 1);
  const list = Array.isArray(options) && options.length ? options : [];
  const sorted = [...list].sort(
    (a, b) => Number(a.days || a.id || 0) - Number(b.days || b.id || 0)
  );
  const fit = sorted.find((o) => Number(o.days || o.id || 0) >= need);
  return String(fit?.id || sorted[sorted.length - 1]?.id || "3");
}

/**
 * Smallest Hourly Care Pass pack that covers `requiredBookings`.
 * Pack sizes come from catalog options (backend/config) — never invent custom prices.
 * Exact match preferred; otherwise next higher pack (e.g. 10 → 15).
 */
export function resolveMinimumCoveringHourlyPack(requiredBookings, options = []) {
  const need = Math.max(1, Math.floor(Number(requiredBookings)) || 1);
  const list = (Array.isArray(options) ? options : [])
    .map((o) => {
      const bookings = Number(o?.bookings != null ? o.bookings : o?.id);
      return {
        id: String(o?.id ?? ""),
        bookings: Number.isFinite(bookings) ? Math.floor(bookings) : 0,
        price: Number(o?.price) || 0,
        label: o?.label || (Number.isFinite(bookings) ? `${Math.floor(bookings)} Bookings` : ""),
      };
    })
    .filter((o) => o.id && o.bookings > 0)
    .sort((a, b) => a.bookings - b.bookings || a.price - b.price);
  if (!list.length) return null;
  const fit = list.find((o) => o.bookings >= need) || list[list.length - 1];
  return {
    id: fit.id,
    bookings: fit.bookings,
    price: fit.price,
    label: fit.label,
    coversExactly: fit.bookings === need,
    coversRequirement: fit.bookings >= need,
    requiredBookings: need,
  };
}

/** Prefer the smallest hourly pack that covers required visits (from catalog options). */
export function preferredHourlyPackIdForVisits(bookingDays, options = []) {
  const pack = resolveMinimumCoveringHourlyPack(bookingDays, options);
  return String(pack?.id || "1");
}

/** User-facing reason when a Care Pass does not cover this shift / day count. */
export function carePassMissingForBookingMessage(subscription, searchShift, bookingDays = null) {
  const summary = getCarePassEntitlementSummary(subscription, searchShift, bookingDays);
  const days = Number(bookingDays);
  if (Number.isFinite(days) && days > 30) {
    return "Continuous care longer than one month requires a Quarterly Care Pass for this patient.";
  }
  if (summary.shortfall != null && summary.shortfall > 0) {
    if (summary.shift === "hourly") {
      const current =
        summary.remaining == null
          ? "does not cover this booking"
          : `covers ${hourlyBookingLabel(summary.remaining)}`;
      return `You have selected ${hourlyBookingLabel(summary.required)}${
        summary.required > 1 ? " (recurring)" : ""
      }. Your current Care Pass ${current}. Please select a Care Pass that covers your booking requirement.`;
    }
    return `This booking needs ${summary.requiredLabel}, but your Care Pass has ${summary.remainingLabel}. Choose a plan that covers the full duration.`;
  }
  const shift = String(searchShift || "").toLowerCase();
  if (shift === "hourly") {
    if (Number.isFinite(days) && days > 1) {
      return `You have selected ${hourlyBookingLabel(days)} (recurring). Please select a Care Pass that covers your booking requirement.`;
    }
    return "Hourly booking requires an Hourly Pass for this patient.";
  }
  return "Buy a Short-term or Quarterly Care Pass for this patient to continue booking.";
}
